import type { ApiResponse } from '@/types';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) return '/api';
  if (envUrl.endsWith('/api')) return envUrl;
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = { 'Content-Type': 'application/json', ...options.headers };
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  if (window.Telegram?.WebApp?.initData) {
    (headers as Record<string, string>)['X-Telegram-Init-Data'] = window.Telegram.WebApp.initData;
  }
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  const data: ApiResponse<T> = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Unknown error');
  return data.data as T;
}

// ============================================
// TYPES
// ============================================

export interface DashboardStats {
  totalUsers: number;
  onboardingCompleted: number;
  onboardingRate: number;
  batteryBCompleted: number;
  batteryCCompleted: number;
  totalEvents: number;
  activeEvents: number;
  totalJournalEntries: number;
  usersActiveThisWeek: number;
}

export interface AdminUser {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string;
  onboardingCompleted: boolean;
  primaryArchetype: string | null;
  currentStage: number;
  stageActiveDays: number;
  batteryACompleted: boolean;
  batteryBCompleted: boolean;
  batteryCCompleted: boolean;
  createdAt: string;
  lastActiveAt: string;
}

export interface AdminEvent {
  id: string;
  title: string;
  description: string | null;
  status: string;
  format: string | null;
  startsAt: string;
  maxParticipants: number | null;
  xpReward: number;
  isActive: boolean;
  createdAt: string;
}

export interface AdminQuest {
  id: string;
  questKey: string;
  title: string;
  description: string | null;
  questType: string;
  requirements: Record<string, unknown>;
  rewards: Record<string, unknown>;
  xpReward: number;
  archetypeAffinity: string | null;
  minLevel: number;
  maxLevel: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  createdAt: string;
  participantsCount?: number;
  completedCount?: number;
}

export interface AdminGuild {
  id: string;
  name: string;
  description: string | null;
  emoji: string | null;
  city: string | null;
  guildType: string;
  telegramChatLink: string | null;
  maxMembers: number | null;
  memberCount: number;
  leaderId: string | null;
  leaderName: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface AdminNotification {
  id: string;
  title: string | null;
  message: string;
  target: string;
  sentBy: string | null;
  senderName: string | null;
  sentAt: string;
  recipientCount: number;
}

export interface ExtendedStats extends DashboardStats {
  totalQuests: number;
  activeQuests: number;
  totalGuilds: number;
  totalChronicleEntries: number;
  usersByArchetype: Record<string, number>;
  usersByStage: Record<string, number>;
  registrationsLast7Days: number[];
  activitiesLast7Days: number[];
}

export interface AdminUserDetails {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  role: string;
  onboardingCompleted: boolean;
  primaryArchetype: string | null;
  secondaryArchetype: string | null;
  shadowRisk: string | null;
  currentStage: number;
  stageActiveDays: number;
  paceMode: string;
  batteryACompleted: boolean;
  batteryBCompleted: boolean;
  batteryCCompleted: boolean;
  fatigue: number;
  maxFatigue: number;
  characteristics: {
    bodyEnergy: number;
    attention: number;
    emotionalRegulation: number;
    communication: number;
    trustBonds: number;
    agency: number;
    creativity: number;
    meaning: number;
  };
  subscriptionTier: string;
  createdAt: string;
  lastActiveAt: string;
  questsCompleted: number;
  eventsAttended: number;
  chronicleEntries: number;
}

// ============================================
// API
// ============================================

export async function getDashboard(): Promise<DashboardStats> {
  return request<DashboardStats>('/admin/dashboard');
}

export async function getAdminUsers(params?: {
  search?: string;
  role?: string;
  limit?: number;
  offset?: number;
}): Promise<{ users: AdminUser[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.role) qs.set('role', params.role);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  return request<{ users: AdminUser[]; total: number }>(`/admin/users?${qs.toString()}`);
}

export async function updateUserRole(userId: string, role: string): Promise<void> {
  await request<{ message: string }>(`/admin/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function getAdminEvents(params?: {
  status?: string;
  search?: string;
}): Promise<{ events: AdminEvent[] }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.search) qs.set('search', params.search);
  return request<{ events: AdminEvent[] }>(`/admin/events?${qs.toString()}`);
}

export async function createAdminEvent(data: Record<string, unknown>): Promise<{ event: AdminEvent }> {
  return request<{ event: AdminEvent }>('/admin/events', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminEvent(eventId: string, data: Record<string, unknown>): Promise<{ event: AdminEvent }> {
  return request<{ event: AdminEvent }>(`/admin/events/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getEventParticipants(eventId: string): Promise<{ participants: Array<{
  userId: string;
  username: string | null;
  firstName: string | null;
  status: string;
  registeredAt: string;
  checkedInAt: string | null;
}> }> {
  return request(`/admin/events/${eventId}/participants`);
}

export async function deleteAdminEvent(eventId: string): Promise<void> {
  await request<{ message: string }>(`/admin/events/${eventId}`, { method: 'DELETE' });
}

// ============================================
// USER DETAILS
// ============================================

export async function getUserDetails(userId: string): Promise<{ user: AdminUserDetails }> {
  return request<{ user: AdminUserDetails }>(`/admin/users/${userId}`);
}

// ============================================
// QUESTS
// ============================================

export async function getAdminQuests(params?: {
  search?: string;
  questType?: string;
  isActive?: boolean;
}): Promise<{ quests: AdminQuest[] }> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.questType) qs.set('questType', params.questType);
  if (params?.isActive !== undefined) qs.set('isActive', String(params.isActive));
  return request<{ quests: AdminQuest[] }>(`/admin/quests?${qs.toString()}`);
}

export async function createAdminQuest(data: {
  questKey: string;
  title: string;
  description?: string;
  questType: string;
  requirements?: Record<string, unknown>;
  rewards?: Record<string, unknown>;
  xpReward?: number;
  archetypeAffinity?: string;
  minLevel?: number;
  maxLevel?: number;
  startDate?: string;
  endDate?: string;
}): Promise<{ quest: AdminQuest }> {
  return request<{ quest: AdminQuest }>('/admin/quests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminQuest(questId: string, data: Partial<{
  title: string;
  description: string;
  questType: string;
  requirements: Record<string, unknown>;
  rewards: Record<string, unknown>;
  xpReward: number;
  archetypeAffinity: string;
  minLevel: number;
  maxLevel: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
}>): Promise<{ quest: AdminQuest }> {
  return request<{ quest: AdminQuest }>(`/admin/quests/${questId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminQuest(questId: string): Promise<void> {
  await request<{ message: string }>(`/admin/quests/${questId}`, { method: 'DELETE' });
}

// ============================================
// GUILDS
// ============================================

export async function getAdminGuilds(params?: {
  search?: string;
  guildType?: string;
  city?: string;
}): Promise<{ guilds: AdminGuild[] }> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.guildType) qs.set('guildType', params.guildType);
  if (params?.city) qs.set('city', params.city);
  return request<{ guilds: AdminGuild[] }>(`/admin/guilds?${qs.toString()}`);
}

export async function createAdminGuild(data: {
  name: string;
  description?: string;
  emoji?: string;
  city?: string;
  guildType?: string;
  telegramChatLink?: string;
  maxMembers?: number;
  leaderId?: string;
}): Promise<{ guild: AdminGuild }> {
  return request<{ guild: AdminGuild }>('/admin/guilds', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminGuild(guildId: string, data: Partial<{
  name: string;
  description: string;
  emoji: string;
  city: string;
  guildType: string;
  telegramChatLink: string;
  maxMembers: number;
  leaderId: string;
  isActive: boolean;
}>): Promise<{ guild: AdminGuild }> {
  return request<{ guild: AdminGuild }>(`/admin/guilds/${guildId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminGuild(guildId: string): Promise<void> {
  await request<{ message: string }>(`/admin/guilds/${guildId}`, { method: 'DELETE' });
}

export async function getGuildMembers(guildId: string): Promise<{ members: Array<{
  userId: string;
  username: string | null;
  firstName: string | null;
  role: string;
  joinedAt: string;
}> }> {
  return request(`/admin/guilds/${guildId}/members`);
}

// ============================================
// EXTENDED STATS
// ============================================

export async function getExtendedStats(): Promise<ExtendedStats> {
  return request<ExtendedStats>('/admin/stats');
}

// ============================================
// NOTIFICATIONS
// ============================================

export async function getNotifications(limit = 50): Promise<{ notifications: AdminNotification[] }> {
  return request<{ notifications: AdminNotification[] }>(`/admin/notifications?limit=${limit}`);
}

export async function sendNotification(data: {
  title?: string;
  message: string;
  target?: string;
}): Promise<{ notification: AdminNotification; recipientCount: number; message: string }> {
  return request<{ notification: AdminNotification; recipientCount: number; message: string }>('/admin/notifications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
